import { useUpdateJobStateMutation } from '@/modules/jobs/mutations/useUpdateJobStateMutation';
import type { JobApi } from '@/modules/jobs/types';
import { Button } from '@/modules/ui/components/button/Button';
import { InlineError } from '@/modules/ui/components/inline-error/InlineError';
import {
  PortalDialog,
  PortalDialogContent,
  PortalDialogDescription,
  PortalDialogFooter,
  PortalDialogHeader,
  PortalDialogTitle,
} from '@/modules/ui/components/portal-dialog/PortalDialog';
import { actionErrorMessage } from '@/utils/actionErrorMessage';

interface ToggleSearchDialogProps {
  job: JobApi;
  /**
   * Fixed when the dialog opens: a successful change flips `job.state` while
   * the dialog is still fading out, which would swap its copy mid-exit.
   */
  resume: boolean;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The dialog has no trigger of its own, so the caller returns focus. */
  onCloseAutoFocus: (event: Event) => void;
}

/**
 * Confirms pausing a live search, or resuming a paused one. The header's
 * state badge confirms success; a failure stays in the dialog.
 */
export function ToggleSearchDialog({
  job,
  resume,
  open,
  onOpenChange,
  onCloseAutoFocus,
}: ToggleSearchDialogProps) {
  const updateState = useUpdateJobStateMutation();
  const isSaving = updateState.isPending;

  const changeOpen = (next: boolean) => {
    if (isSaving) return;
    // Reopening starts clean instead of showing the last attempt's error.
    if (!next) updateState.reset();
    onOpenChange(next);
  };

  const handleConfirm = () => {
    updateState.mutate(
      { jobId: job.id, state: resume ? 'Published' : 'OnHold' },
      { onSuccess: () => changeOpen(false) },
    );
  };

  return (
    <PortalDialog onOpenChange={changeOpen} open={open}>
      <PortalDialogContent onCloseAutoFocus={onCloseAutoFocus}>
        <PortalDialogHeader>
          <PortalDialogTitle>
            {resume ? 'Resume' : 'Pause'} the search for {job.title}?
          </PortalDialogTitle>
          <PortalDialogDescription>
            {resume
              ? 'Finlay starts sourcing new candidates and reaching out again. The job counts toward your active jobs; if your plan has no room, pause or close another job first.'
              : 'Finlay stops sourcing new candidates and starting new outreach. Messages already queued may still be sent. Candidates you already have stay here, and you can resume any time.'}
          </PortalDialogDescription>
        </PortalDialogHeader>
        {updateState.isError && (
          <InlineError>
            {actionErrorMessage(
              resume ? 'resume the search' : 'pause the search',
              updateState.error,
            )}
          </InlineError>
        )}
        <PortalDialogFooter>
          <Button
            disabled={isSaving}
            onClick={() => changeOpen(false)}
            size="sm"
            variant="outline"
          >
            Cancel
          </Button>
          <Button loading={isSaving} onClick={handleConfirm} size="sm">
            {resume ? 'Resume search' : 'Pause search'}
          </Button>
        </PortalDialogFooter>
      </PortalDialogContent>
    </PortalDialog>
  );
}
